import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "../../../supabase";
import styles from "./Stadium24.module.css";
import { useQuizOptions } from './hooks/useQuizOptions';
import { fetchStadiumRankings } from './utils/stadiumRankings';
import Textarea from '../../../components/common/Textarea';
import Button from '../../../components/common/Button';


function Stadium24() {
  const { id } = useParams();
  const [stadium24Data, setStadium24Data] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [activeTab, setActiveTab] = useState('reviews');
  const [positiveReviews, setPositiveReviews] = useState([]);
  const [negativeReviews, setNegativeReviews] = useState([]);
  const [activeReviewTab, setActiveReviewTab] = useState('positive');
  const [satisfaction, setSatisfaction] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({ match: false, user: false, review: false });
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const [rankings, setRankings] = useState({});
  const [isLoadingRankings, setIsLoadingRankings] = useState(true);
  const [stadiumStats, setStadiumStats] = useState({ totalStadiums: 0, avgMatches: 0, avgUsers: 0 });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchStadium24Data = async () => {
      setIsLoading(true);
      try {
        // id에서 실제 id 값 추출 (code를 제외한 숫자 부분)
        const actualId = parseInt(id.match(/^\d+/)[0]);
        
        const { data, error } = await supabase
          .from("stadium24")
          .select("*")
          .eq("id", actualId)
          .single();

        if (error) throw error;
        
        // code 검증
        if (data && `${data.id}${data.code}` !== id) {
          setStadium24Data(null); // 잘못된 code인 경우
          return;
        }

        if (data) {
          setStadium24Data(data);
        }
      } catch (error) {
        console.error("Error fetching stadium24 data:", error);
      } finally {
        setTimeout(() => setIsLoading(false), 1500);
      }
    };

    if (id) {
      fetchStadium24Data();
    }
  }, [id]);

  useEffect(() => {
    const fetchStadiumImage = async () => {
      try {
        // stadium24Data.id를 직접 사용하여 API 호출
        const response = await fetch(`https://www.plabfootball.com/api/v2/stadium-groups/${stadium24Data.id}/`);
        const data = await response.json();
        
        if (data.stadiums?.[0]?.images?.[0]?.image) {
          setBackgroundImage(data.stadiums[0].images[0].image);
        }
      } catch (error) {
        console.error('Error fetching stadium image:', error);
      }
    };

    if (stadium24Data) {
      fetchStadiumImage();
    }
  }, [stadium24Data]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!stadium24Data) {
        console.log('stadium24Data가 없음');
        return;
      }

      try {
        const actualId = stadium24Data.id;
        console.log('리뷰 데이터 조회 시작:', actualId);

        const { data, error } = await supabase
          .from("stadium24_review")
          .select(`
            satisfaction,
            review_cnt,
            good_grass, bad_grass,
            good_restroom, bad_restroom,
            good_parking, bad_parking,
            good_waiting, bad_waiting,
            good_access, bad_access,
            good_shower, bad_shower,
            good_info, bad_info
          `)
          .eq("stadium24_id", actualId)
          .maybeSingle();

        console.log('받아온 리뷰 데이터:', data);
        console.log('에러 여부:', error);

        // 데이터가 없는 경우 기본값 설정
        const defaultData = {
          satisfaction: 0,
          review_cnt: 0,
          good_grass: 0, bad_grass: 0,
          good_restroom: 0, bad_restroom: 0,
          good_parking: 0, bad_parking: 0,
          good_waiting: 0, bad_waiting: 0,
          good_access: 0, bad_access: 0,
          good_shower: 0, bad_shower: 0,
          good_info: 0, bad_info: 0
        };

        // data가 없으면 defaultData 사용, 있으면 data 사용
        const reviewData = data || defaultData;

        // 리뷰 데이터 처리
        const positiveData = [];
        const negativeData = [];

        Object.entries(reviewData).forEach(([key, value]) => {
          if (key === 'satisfaction') {
            setSatisfaction(value);
          } else if (key.startsWith('good_') && value > 0) {
            positiveData.push({ name: key, value });
          } else if (key.startsWith('bad_') && value > 0) {
            negativeData.push({ name: key, value });
          }
        });

        // 리뷰 개수 기준으로 내림차순 정렬
        setPositiveReviews(positiveData.sort((a, b) => b.value - a.value));
        setNegativeReviews(negativeData.sort((a, b) => b.value - a.value));

        console.log('가공된 긍정 리뷰:', positiveData);
        console.log('가공된 부정 리뷰:', negativeData);
        console.log('만족도:', satisfaction);

      } catch (error) {
        console.error("리뷰 데이터 처리 중 에러 발생:", error);
        setPositiveReviews([]);
        setNegativeReviews([]);
        setSatisfaction(0);
      }
    };

    fetchReviews();
  }, [stadium24Data?.id]);

  useEffect(() => {
    const loadRankings = async () => {
      setIsLoadingRankings(true);
      try {
        const categories = ['matches', 'reviews', 'grass', 'restroom', 'parking', 
                          'waiting', 'access', 'shower', 'info'];
        const rankingsData = {};
        
        for (const category of categories) {
          rankingsData[category] = await fetchStadiumRankings(category);
        }
        
        setRankings(rankingsData);
      } catch (error) {
        console.error('Error loading rankings:', error);
      } finally {
        setIsLoadingRankings(false);
      }
    };

    loadRankings();
  }, []);

  useEffect(() => {
    const fetchStadiumStats = async () => {
      try {
        const { data, error } = await supabase
          .from("stadium24")
          .select('id, match_all, user_all');
        
        if (error) throw error;
        
        const totalStadiums = data.length;
        const avgMatches = Math.round(data.reduce((sum, stadium) => sum + stadium.match_all, 0) / totalStadiums);
        const avgUsers = Math.round(data.reduce((sum, stadium) => sum + stadium.user_all, 0) / totalStadiums);
        
        setStadiumStats({ totalStadiums, avgMatches, avgUsers });
      } catch (error) {
        console.error("Error fetching stadium stats:", error);
      }
    };

    fetchStadiumStats();
  }, []);

  const matchOptions = useQuizOptions(stadium24Data?.match_all, [stadium24Data]);
  const userOptions = useQuizOptions(stadium24Data?.user_all, [stadium24Data]);
  const reviewOptions = useQuizOptions(satisfaction, [stadium24Data]);

  const showToast = (message, type) => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 1000);
  };

  const handleQuizAnswer = (section, isCorrect) => {
    setQuizAnswers(prev => ({ ...prev, [section]: true }));
    if (isCorrect) {
      showToast("O", "success");
    } else {
      showToast("X", "error");
    }
  };

  const handleSubmitMessage = async () => {
    if (!message.trim()) {
      return;
    }

    try {
      const actualId = parseInt(id.match(/^\d+/)[0]);
      
      const { error } = await supabase
        .from('stadium24_message')
        .insert([
          { 
            stadium24_id: actualId,
            message: message.trim()
          }
        ]);

      if (error) throw error;

      // 성공 시 confirm 표시
      if (window.confirm('소중한 메시지 감사합니다. 새해 복 많이 받으세요!')) {
        // 메시지 초기화
        setMessage('');
      }
    } catch (error) {
      console.error('Error submitting message:', error);
      alert('메시지 전송에 실패했습니다. 다시 시도해주세요.');
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>"STADIUM24"</div>;
  }

  if (!stadium24Data) {
    return (
      <div className={styles.notFound}>
        <h2>"STADIUM24"</h2>
        <p>존재하지 않는 구장입니다.</p>
      </div>
    );
  }

  const userDays = [
    { label: '월', value: stadium24Data.user_mon || 0 },
    { label: '화', value: stadium24Data.user_tue || 0 },
    { label: '수', value: stadium24Data.user_wed || 0 },
    { label: '목', value: stadium24Data.user_thu || 0 },
    { label: '금', value: stadium24Data.user_fri || 0 },
    { label: '토', value: stadium24Data.user_sat || 0 },
    { label: '일', value: stadium24Data.user_sun || 0 },
  ];
  const matchDays = [
    { label: '월', value: stadium24Data.match_mon || 0 },
    { label: '화', value: stadium24Data.match_tue || 0 },
    { label: '수', value: stadium24Data.match_wed || 0 },
    { label: '목', value: stadium24Data.match_thu || 0 },
    { label: '금', value: stadium24Data.match_fri || 0 },
    { label: '토', value: stadium24Data.match_sat || 0 },
    { label: '일', value: stadium24Data.match_sun || 0 },
  ];

  const maxUser = Math.max(...userDays.map(day => day.value), 1);
  const maxMatch = Math.max(...matchDays.map(day => day.value), 1);


  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleReviewTabChange = (tab) => {
    setActiveReviewTab(tab);
  };

  const reviewNameMap = {
    good_info: '시설 정보가 정확해요',
    good_grass: '잔디 상태가 좋아요',
    good_restroom: '화장실이 깨끗해요',
    good_shower: '샤워 시설이 쾌적해요',
    good_waiting: '대기 공간이 쾌적해요',
    good_parking: '주차가 편리해요',
    good_access: '접근성이 좋아요',
    bad_info: '시설 정보와 달라요',
    bad_grass: '잔디 상태가 안좋아요',
    bad_restroom: '화장실이 깨끗하지 않아요',
    bad_shower: '샤워 시설이 아쉬워요',
    bad_waiting: '대기 공간이 불편해요',
    bad_parking: '주차하기 불편해요',
    bad_access: '접근성이 안좋아요',
  };

  const totalPositiveReviewValue = [...positiveReviews].reduce((sum, review) => sum + review.value, 0);
  const totalNegativeReviewValue = [...negativeReviews].reduce((sum, review) => sum + review.value, 0);

  const numberFormatter = new Intl.NumberFormat('en-US');  // 숫자 포맷터 생성

  console.log('satisfaction value:', satisfaction, typeof satisfaction);

  return (
    <>
      <Helmet>
        <title>{stadium24Data.name} - Stadium24</title>
      </Helmet>
        <div className={styles.container}>
      {/* 토스트 메시지 */}
      <div className={`${styles.toast} ${toast.visible ? styles.show : ''} ${styles[toast.type]}`}>
        {toast.message}
      </div>

      {/* 섹션 1: 커버 */}
      <section className={styles.cover} style={{ backgroundImage: `url(${backgroundImage})` }}>
        <div className={styles.coverOverlay}>
          <div className={styles.coverContent}>
            <h3>2024년 스타디움 연말결산</h3>
            <h1>{stadium24Data.name}</h1>
            <p>구장주님, 2024년 플랩풋볼과 함께해 주셔서 진심으로 감사드립니다. {stadium24Data.name}과 함께 전국 곳곳에서 그 어느 때보다 많은 사람들이 풋살을 즐기며 활기찬 일상을 보낼 수 있었던 2024년이었습니다.</p>
          </div>
          <div className={styles.scrollDown}>
            <div className={styles.scrollIcon}>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </section>
        <div className={styles.sectionGroup}>
      {/* 섹션 2: 매치 통계 */}
      <section className={styles.matches}>
        <div className={styles.sectionHeader}>
            <h2>2024년, {stadium24Data.name}에서 진행된 소셜 매치는 몇 경기일까요?</h2>
        </div>
        <div className={styles.sectionBody}>
            <div className={styles.quiz}>
                {!quizAnswers.match && (
                    <div className={styles.quizOptions}>
                        {matchOptions.map((option, index) => (
                            <button
                                key={index}
                                onClick={() => handleQuizAnswer('match', option === stadium24Data.match_all)}
                                disabled={quizAnswers.match}
                            >
                                {numberFormatter.format(option)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {quizAnswers.match && (
              <div className={styles.quizAnswer}>
                <div className={styles.singleMainValue}>
                  <p className={styles.totalValue}>{numberFormatter.format(stadium24Data.match_all)}</p>
                  <div>
                    {stadium24Data.match_all >= 380 ? (
                      <p className={styles.description}>2024년 <span className={styles.highlight}>구장당 평균 경기 수는 {numberFormatter.format(stadiumStats.avgMatches)}경기</span>였어요. {stadium24Data.name}에서는 {stadium24Data.match_all} 경기가 열리며 평균보다 {Math.round((stadium24Data.match_all / stadiumStats.avgMatches * 100) - 100)}%나 더 많은 경기가 진행됐어요.
                        <br/><br/>프리미어리그에서는 한 시즌에 총 380경기가 열린다고 해요. 2024년 한 해 동안 {stadium24Data.name}에서만 프리미어리그 {Math.floor(stadium24Data.match_all / 380)}시즌만큼의 많은 소셜 매치가 열렸어요! 🎉
                      </p>
                    ) : (
                      <p className={styles.description}>
                        2024년 전국에서는 구장당 <span className={styles.highlight}>평균 {numberFormatter.format(stadiumStats.avgMatches)}경기</span>가 진행됐어요. <br/><br/>앞으로 구장 관리 사이트를 통해 다양한 통계와 운영에 도움 되는 정보를 제공해 드릴 예정이에요. 2025년에는 플랩풋볼과 함께 더욱 많은 매치를 열어봐요!
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.chartContainer}>
                <h3>요일별 매치수</h3>
                <div className={styles.chart}>
                  {matchDays.map((day, index) => (
                    <div key={index} className={styles.barContainer}>
                      <div
                        className={styles.bar}
                        style={{ height: `${(day.value / maxMatch) * 100}%` }}
                      />
                      <span className={styles.label}>{day.label}</span>
                      <span className={styles.value}>{numberFormatter.format(day.value)}</span>
                    </div>
                  ))}
                </div>
                  </div>
              </div>
            )}
        </div>
      </section>

      {/* 섹션 3: 방문자 통계 */}
      <section className={styles.users}>    
        <div className={styles.sectionHeader}>
            <h2>2024년, {stadium24Data.name}에 몇 명이 방문했을까요?</h2>
        </div>
        <div className={styles.sectionBody}>
        <div className={styles.quiz}>
            {!quizAnswers.user && (
                <div className={styles.quizOptions}>
                    {userOptions.map((option, index) => (
                        <button key={index} onClick={() => handleQuizAnswer('user', option === stadium24Data.user_all)} disabled={quizAnswers.user}>
                            {numberFormatter.format(option)}
                        </button>
                    ))}
                </div>
            )}
            </div>
            {quizAnswers.user && (
            <div className={styles.quizAnswer}>
                <div className={styles.mainValueContainer}>
                    <div className={styles.doubleMainValue}>
                        <h3>전체 방문자 수</h3>
                        <p className={styles.totalValue}>{numberFormatter.format(stadium24Data.user_all)}</p>
                    </div>
                    <div className={styles.doubleMainValue}>
                        <h3>순 방문자 수</h3>
                        <p className={styles.totalValue}>{numberFormatter.format(stadium24Data.user_unique_all)}</p>
                    </div>

                </div>
                <div className={styles.description}>
                      <p className={styles.description}>
                      2024년 <span className={styles.highlight}>구장당 평균 방문자 수는 {numberFormatter.format(stadiumStats.avgUsers)}명</span>이에요.
                      </p>
                    <p>{stadium24Data.name}에는 {numberFormatter.format(stadium24Data.user_all)}명의 유저가 방문하여 건강하고 활기찬 일상을 즐길 수 있었어요. 🥰</p>
                </div>
                <div className={styles.chartContainer}>
                  <h3>요일별 방문자수</h3>
                  <div className={styles.chart}>
                  {userDays.map((day, index) => (
                      <div key={index} className={styles.barContainer}>
                      <div 
                          className={styles.bar} 
                          style={{ height: `${(day.value / maxUser) * 100}%` }}
                      />
                      <span className={styles.label}>{day.label}</span>
                      <span className={styles.value}>{numberFormatter.format(day.value)}</span>
                      </div>
                  ))}
                  </div>
                </div>
            </div>
            )}
        </div>
      </section>

      {/* 섹션 4: 리뷰 통계 */}
      {stadium24Data && satisfaction !== null && satisfaction !== 0 && (
        <section className={styles.reviews}>
          <div className={styles.sectionHeader}>
            <h2>2024년, {stadium24Data.name}의 구장 만족도는 몇 점일까요?</h2>
          </div>
          <div className={styles.sectionBody}>
            <div className={styles.quiz}>
                {!quizAnswers.review && (
                    <div className={styles.quizOptions}>
                        {reviewOptions.map((option, index) => (
                            <button
                                key={index}
                                onClick={() => handleQuizAnswer('review', Number(option) === Number(satisfaction))}
                                disabled={quizAnswers.review}
                            >
                                {option}%
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {quizAnswers.review && (
            <div className={styles.quizAnswer}>
                <div className={styles.singleMainValue}>
                    <p className={styles.totalValue}>
                        {satisfaction !== null ? `${satisfaction}%` : 'N/A'}
                        {satisfaction >= 90 && <span className={styles.greatBadge}>Great</span>}
                        {satisfaction >= 75 && satisfaction < 90 && <span className={styles.goodBadge}>Good</span>}
                    </p>
                    <p className={styles.description}>
                        2024년 전국 구장의 <span className={styles.highlight}>평균 만족도는 75.4%</span>예요. <br/><br/>
                        {stadium24Data.name} 구장을 방문한 유저들이 남겨주신 의견을 조금 더 자세하게 모아봤어요. 리뷰를 참고해서 더 좋은 서비스를 제공한다면 
                        더욱 사랑받는 {stadium24Data.name} 구장이 될 수 있어요.
                    </p>
                </div>
                <div className={styles.tabs}>
                <button onClick={() => handleReviewTabChange('positive')} className={activeReviewTab === 'positive' ? styles.active : ''}>😀 좋아요</button>
                <button onClick={() => handleReviewTabChange('negative')} className={activeReviewTab === 'negative' ? styles.active : ''}>😞 아쉬워요</button>
                </div>
                <div className={styles.reviewLists}>
                {activeReviewTab === 'positive' ? (
                    <div className={styles.positiveReviews}>
                        {positiveReviews.length > 0 ? (
                            positiveReviews.map((review, index) => (
                                <div key={index} className={styles.reviewItem}>
                                    <div className={styles.reviewContainer}>
                                        <div 
                                            className={styles.reviewBar} 
                                            style={{ width: `${(review.value / totalPositiveReviewValue) * 100}%` }}
                                        >
                                            <div className={styles.reviewTextContainer}>
                                                <span className={styles.reviewText}>
                                                    {reviewNameMap[review.name] || review.name}
                                                </span>
                                                <span className={styles.reviewValue}>
                                                    {numberFormatter.format(review.value)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.noReviews}>등록된 리뷰가 없어요.</div>
                        )}
                    </div>
                ) : (
                    <div className={styles.negativeReviews}>
                        {negativeReviews.length > 0 ? (
                            negativeReviews.map((review, index) => (
                                <div key={index} className={styles.reviewItem}>
                                    <div className={styles.reviewContainer}>
                                        <div 
                                            className={styles.reviewBar} 
                                            style={{ width: `${(review.value / totalNegativeReviewValue) * 100}%` }}
                                        >
                                            <span className={styles.reviewText}>
                                                {reviewNameMap[review.name] || review.name}
                                            </span>
                                            <span className={styles.reviewValue}>
                                                {numberFormatter.format(review.value)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.noReviews}>등록된 리뷰가 없어요.</div>
                        )}
                    </div>
                )}
                </div>
            </div>
            )}
          </div>
        </section>
      )}

      {/* 섹션 5: 스타디움 TOP 10 */}
      <section className={styles.topStadiums}>
        <div className={styles.sectionHeader}>
          <h2>🏆 Stadium24 Award</h2>
          <div className={styles.description}>
            <p>2024년에는 어떤 구장들이 유저들에게 사랑 받았을까요?</p>
          </div>
          <div className={styles.subDescription}>
            <p>순위가 같을 경우 리뷰가 많은 구장이 더욱 높은 순위를 받아요.</p>
          </div>
        </div>
        <div className={styles.sectionBody}>
            <div className={styles.tabs}>
            <button onClick={() => handleTabChange('reviews')} className={activeTab === 'reviews' ? styles.active : ''}>🏟️ 만족도</button>
            <button onClick={() => handleTabChange('grass')} className={activeTab === 'grass' ? styles.active : ''}>🌿 잔디</button>
            <button onClick={() => handleTabChange('restroom')} className={activeTab === 'restroom' ? styles.active : ''}>🚻 화장실</button>
            <button onClick={() => handleTabChange('parking')} className={activeTab === 'parking' ? styles.active : ''}>🅿️ 주차</button>
            <button onClick={() => handleTabChange('waiting')} className={activeTab === 'waiting' ? styles.active : ''}>🪑 대기공간</button>
            {/* <button onClick={() => handleTabChange('access')} className={activeTab === 'access' ? styles.active : ''}>🚶 접근성</button> */}
            <button onClick={() => handleTabChange('shower')} className={activeTab === 'shower' ? styles.active : ''}>🚿 샤워실</button>
            {/* <button onClick={() => handleTabChange('info')} className={activeTab === 'info' ? styles.active : ''}>ℹ️ 정보</button> */}
            </div>
            {isLoadingRankings ? (
              <div className={styles.loading}>로딩 중...</div>
            ) : (
              <ul className={styles.stadiumList}>
                {rankings[activeTab]?.map((stadium, index) => (
                  <li key={stadium.id} className={styles.stadiumItem}>
                    <span className={styles.stadiumRank}>{index + 1}</span>
                    <div className={styles.stadiumInfo}>
                      <div className={styles.stadiumNameWrapper}>
                        <span className={styles.stadiumName}>{stadium.name}</span>
                        {stadium.isRental === 1 && <span className={styles.rentalBadge}>구장예약</span>}
                        {stadium.isLaundry === 1 && <span className={styles.laundryBadge}>런드리</span>}
                      </div>
                      <span className={styles.stadiumValue}>
                        {stadium.value > 0 ? `${stadium.value}${stadium.unit}` : ''}
                        {stadium.reviewCount > 0 && (
                          <span className={styles.reviewCount}>
                            ({stadium.reviewCount}개의 리뷰)
                          </span>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </section>
      <section className={styles.footer}>
        <div className={styles.sectionHeader}>
        <img src='https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/common/logo_plab_eng.svg' alt="플랩풋볼 로고" />
          <h2>행복한 2025년이 되었으면 좋겠습니다.</h2>
        </div>
        <div className={styles.footerContent}>
          <p className={styles.description}>플랩풋볼은 "많은 사람들이 스포츠가 주는 다양한 감정을 즐기며 살아가도록"이라는 가치를 전달하기 위해 노력하고 있어요. 2025년에도 {stadium24Data.name} 구장과 함께 많은 사람들에게 일상의 활력과 즐거움을 나눌 수 있도록 노력하겠습니다. <br/><br/>새해 복 많이 받으세요!</p>
          <p className={styles.description}>플랩풋볼 드림</p>
          <div className={styles.footerForm}>
            <Textarea 
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="플랩풋볼에 전달하고 싶은 이야기가 있다면 이곳에 남겨주세요 :)"
            />
            <Button 
              size="large" 
              width="full-width"
              onClick={handleSubmitMessage}
            >
              보내기
            </Button>
          </div>
        </div>
      </section>

        </div>

</div>
    </>
  );
}

export default Stadium24; 